import {
  EventType,
  IEventable,
  IEventTimePeriod,
  isAppointment,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  getTimePeriodDuration,
  ITimePeriod,
  TimeBucket,
  Timezone,
  timePeriodsAdjacent,
  timePeriodsIntersect,
  toNamedDocument,
  toTimePeriod,
} from '@principle-theorem/shared';
import { get, omit } from 'lodash';
import { unitOfTime } from 'moment-timezone';
import { TimezoneResolver } from '../../timezone';
import { stafferToNamedDoc } from '../common';
import { Event } from './event';

export interface IEventablePeriod {
  eventable: IEventable;
  period: ITimePeriod;
}

export class EventTimePeriod {
  static init(
    overrides: AtLeast<IEventTimePeriod, 'from' | 'to' | 'practice' | 'staffer'>
  ): IEventTimePeriod {
    return {
      practice: toNamedDocument(overrides.practice),
      staffer: stafferToNamedDoc(overrides.staffer),
      overlappingEvents: [],
      adjacentEvents: [],
      overlapDuration: 0,
      ...omit(overrides, ['practice', 'staffer']),
    };
  }

  /**
   * Resolves each eventable's period once so it can be reused across many
   * comparisons. The predicates below never mutate the periods, so sharing is
   * safe.
   */
  static toEventablePeriods(
    eventables: IEventable[],
    timezone?: Timezone
  ): IEventablePeriod[] {
    return eventables.map((eventable) => ({
      eventable,
      period: toTimePeriod(eventable.event.from, eventable.event.to, timezone),
    }));
  }

  /**
   * Resolution depends only on the eventable and the timezone, not on the time
   * being compared, so periods are built once per timezone.
   */
  static getCachedEventablePeriods(
    cache: Map<string, IEventablePeriod[]>,
    eventables: IEventable[],
    time: ITimePeriod
  ): IEventablePeriod[] {
    const timezone = TimezoneResolver.fromMoment(time);
    const cacheKey = timezone ?? '';
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const eventablePeriods = EventTimePeriod.toEventablePeriods(
      eventables,
      timezone
    );
    cache.set(cacheKey, eventablePeriods);
    return eventablePeriods;
  }

  static eventableOverlapsRange(
    eventable: IEventable,
    time: ITimePeriod
  ): boolean {
    return timePeriodsIntersect(
      Event.toTimePeriod(eventable.event, TimezoneResolver.fromMoment(time)),
      time,
      false,
      'minutes'
    );
  }

  static findOverlappingFromPeriods(
    eventablePeriods: IEventablePeriod[],
    time: ITimePeriod
  ): IEventable[] {
    return eventablePeriods
      .filter((eventablePeriod) =>
        timePeriodsIntersect(eventablePeriod.period, time, false, 'minutes')
      )
      .map((eventablePeriod) => eventablePeriod.eventable);
  }

  static findAdjacentFromPeriods(
    eventablePeriods: IEventablePeriod[],
    time: ITimePeriod
  ): IEventable[] {
    return eventablePeriods
      .filter((eventablePeriod) =>
        timePeriodsAdjacent(eventablePeriod.period, time, 1, 'minutes')
      )
      .map((eventablePeriod) => eventablePeriod.eventable);
  }

  static findOverlappingEventables(
    currentlyUsedTimes: IEventable[],
    time: ITimePeriod
  ): IEventable[] {
    return EventTimePeriod.findOverlappingFromPeriods(
      EventTimePeriod.toEventablePeriods(
        currentlyUsedTimes,
        TimezoneResolver.fromMoment(time)
      ),
      time
    );
  }

  static hasOverlappingEventables(
    currentlyUsedTimes: IEventable[],
    time: ITimePeriod
  ): boolean {
    return currentlyUsedTimes.some((currentlyUsedTime) =>
      EventTimePeriod.eventableOverlapsRange(currentlyUsedTime, time)
    );
  }

  static calculateOverlapDuration(
    overlappingEvents: IEventable[],
    time: ITimePeriod,
    unit: unitOfTime.Base = 'minutes'
  ): number {
    return TimeBucket.fromEvents(
      overlappingEvents
        .filter(
          (overlappingEvent) =>
            isAppointment(overlappingEvent) ||
            (get(overlappingEvent, 'isBlocking', true) &&
              overlappingEvent.event.type !== EventType.RosteredOn)
        )
        .map((overlappingEvent) => overlappingEvent.event),
      TimezoneResolver.fromMoment(time)
    )
      .betweenTime(time.from, time.to, true)
      .mergeOverlapping()
      .get()
      .map((overlappingTime) => getTimePeriodDuration(overlappingTime, unit))
      .reduce((total, timeDuration) => total + timeDuration, 0);
  }

  static findAdjacentEventables(
    events: IEventable[],
    time: ITimePeriod
  ): IEventable[] {
    return EventTimePeriod.findAdjacentFromPeriods(
      EventTimePeriod.toEventablePeriods(
        events,
        TimezoneResolver.fromMoment(time)
      ),
      time
    );
  }
}
